// skipcq: JS-0128 - deepsource is falsely flagging React as unused import 🤷
import React, { useEffect, useState, useCallback } from 'react';
import { Text, Title, Stack, BodyText } from '@lessonnine/design-system.lib';
import tokens from '@lessonnine/design-tokens.lib/dist/json/variables.json';

import { ResetPasswordPageContainer, ResetPasswordPageCard, ResetPasswordPageFormContainer } from '../../App/index.styled';
import NavBar from '../../App/NavBar';
import TrackableButton from 'src/viewLayer/components/shared/TrackableButton';
import Footer from 'src/viewLayer/components/pages/CardLayout/Footer';
import { t } from 'src/viewLayer/helpers/translations';
import { getReturnToUrl } from 'src/dataLayer/localData/queryParams';
import useSubmitPasswordReset from 'src/viewLayer/components/hooks/useSubmitPasswordReset';
import { CaptchaPasswordReset } from 'src/viewLayer/components/CaptchaPasswordReset/CaptchaPasswordReset';
import PasswordInput from 'src/viewLayer/components/shared/Inputs/PasswordInput';
import { trackAuthenticationResetPassword } from 'src/dataLayer/remoteData/babbelEventStream';

const STATUS_IDLE = 'idle';
const STATUS_LOADING = 'loading';
const STATUS_SUCCESS = 'success';

export default function PasswordResetPage() {
    const [resetPasswordRequestStatus, setResetPasswordRequestStatus] = useState(STATUS_IDLE);
    const [captchaToken, setCaptchaToken] = useState();
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [errorKey, setErrorKey] = useState('');
    const [passwordResetToken, setPasswordResetToken] = useState();
    const [isButtonClicked, setIsButtonClicked] = useState(false);
    const { 
        makeResetPasswordRequest, 
        isResetPasswordRequestInProgress
    } = useSubmitPasswordReset({ 
        captchaResponseToken: captchaToken, 
        password, 
        confirmPassword, 
        passwordResetToken 
    });
    const params = new URLSearchParams(window.location.search);
    const isActionInProgress = isResetPasswordRequestInProgress || resetPasswordRequestStatus === STATUS_LOADING || isButtonClicked;
    const isPasswordMatching = () => password && confirmPassword && password === confirmPassword && password.length >= 6;

    useEffect(() => {
        const passwordResetTokenParam = params.get('token');
        if (passwordResetTokenParam) {
            setPasswordResetToken(passwordResetTokenParam);
        }
        trackAuthenticationResetPassword({stage: 'reset-page-shown'});
    }, []);

    useEffect(() => {
        if (resetPasswordRequestStatus === STATUS_SUCCESS) {
            trackAuthenticationResetPassword({stage: 'success-page-shown'});
        }
    }, [resetPasswordRequestStatus]);

    useEffect(() => {
        if (!captchaToken || !isButtonClicked) { return; }
        setIsButtonClicked(false);

        (async () => {
            setResetPasswordRequestStatus(STATUS_LOADING);
            setErrorKey('');

            const result = await makeResetPasswordRequest({
                captchaResponseToken: captchaToken,
                password,
                confirmPassword,
                passwordResetToken
            });
            trackAuthenticationResetPassword({stage: 'submitted'});

            if (result.success) {
                setResetPasswordRequestStatus(STATUS_SUCCESS);
            } else {
                setErrorKey(result.error || 'An error occurred');
                setResetPasswordRequestStatus(STATUS_IDLE);
                setPassword('');
                setConfirmPassword('');
                setCaptchaToken('');
            }
        })();
    }, [captchaToken, isButtonClicked, makeResetPasswordRequest]);

    const handleResetPassword = useCallback(() => {
        setIsButtonClicked(true);
    }, []);
    
    const redirectToDashboard = useCallback(() => {
        const redirectUrl = getReturnToUrl(window.location.search, false);
        window.location.replace(redirectUrl);
    }, []);
    
    const handlePasswordChange = useCallback((e) => {
        setPassword(e.target.value);
    }, []);
    
    const handleConfirmPasswordChange = useCallback((e) => {
        setConfirmPassword(e.target.value);
    }, []);
    return (
        <>
            <NavBar />
            <ResetPasswordPageContainer >
                <ResetPasswordPageCard padding={{ xxsmall: [36, 8, 10], small: [80, 8, 24]}} gap={24}>
                    <Title size="small" textAlign="center">{resetPasswordRequestStatus === 'success' ? t('change_password_page.reset_password_title') : t('change_password_page.title')}</Title>
                    {resetPasswordRequestStatus === 'success' ? (
                        <>
                        <Text textAlign="center">{t('change_password_page.success_confirmation')}</Text>
                        <TrackableButton
                            disabled={isActionInProgress}
                            fullWidth
                            variant="primary"
                            onClick={redirectToDashboard}
                        >
                            {t('change_password_page.button_ok')}
                        </TrackableButton>
                        </>
                    ) : (
                        <>
                            <Text textAlign="center">{t('change_password_page.description')}</Text>
                            <ResetPasswordPageFormContainer gap={24}>
                                <Stack padding={18} gap={15} alignItems="center">
                                    <PasswordInput 
                                        value={password}
                                        onChangeHandler={handlePasswordChange}
                                        isFocused
                                        disabled={isActionInProgress}
                                        label={t('password.placeholder')}
                                        placeholder={t('change_password_page.placeholder_password_input')}
                                        id="uat-password-input-field"
                                    />
                                    <PasswordInput
                                        value={confirmPassword}
                                        onChangeHandler={handleConfirmPasswordChange}
                                        disabled={isActionInProgress}
                                        label={t('change_password_page.change_password_confirm')}
                                        placeholder={t('change_password_page.placeholder_password_confirmation_input')}
                                        id="uat-confirm-password-input-field"
                                    />
                                    {errorKey && <BodyText size="small" textAlign="center" color={tokens.color.theme.dark.reference.red.red60} as="p">{t(errorKey)}</BodyText>}
                                    <TrackableButton
                                        id="uat-forgot-password-button"
                                        dataTestid="forgot-password-page-submit-button"
                                        disabled={isActionInProgress || !isPasswordMatching()}
                                        title="Reset password button"
                                        trackingId="reset_password_submit"
                                        inProgress={isActionInProgress}
                                        fullWidth
                                        variant="primary"
                                        onClick={handleResetPassword}
                                    >
                                        {t('change_password_page.button_change_password')}
                                    </TrackableButton>
                                </Stack>
                            </ResetPasswordPageFormContainer>
                        </>
                    )}
                </ResetPasswordPageCard>
                <CaptchaPasswordReset setCaptchaToken={setCaptchaToken} shouldExecuteCaptcha={isButtonClicked} shouldTrackTokenRequested={false} />
                <Footer />
            </ResetPasswordPageContainer>
        </>
    );
};
